import { dispatcher, DispatcherEvents } from "./dispatcher";
import { cleanSessionAndPassword } from "./helpers";

/**
 * additionalErrors should contain the server stack traces and a client error reference
 */
export interface IExtendedError extends Error {
    apiCall?: string;
    additionalErrors?: {}[];
}

export enum ErrorType {
    JS = "JS",
    API = "API",
    REQUIREJS = "REQUIREJS",
    NATIVEJS = "NATIVEJS"
}

export interface IRawError {
    error?: Error;
    message?: string | object;
    url?: string | null;
    lineNumber?: number | null;
    column?: number | null;
    silenceError?: boolean;
}

export function sendToErrorTracker(err: IExtendedError, tag: string): void {
    dispatcher.trigger(DispatcherEvents.ErrorSendToErrorTracker, err, tag);
}

export function handleJS(message: string | object, url: string | null, lineNumber: number | null, column: number | null, error: Error | null | undefined, tag = "javascript"): void {
    dispatcher.trigger(DispatcherEvents.ErrorHandleJS, message, url, lineNumber, column, error, tag);
}

/**
 * Reports uncaught JavaScript errors through handleJS, for the lifetime of the page.
 *
 * Drive assigns window.onerror, as it always has. Add-ins depend on that: some call window.onerror(...)
 * themselves to hand an error they caught to Drive (Fleet Profit Center add-ins do), and some chain it
 * (GoRoute saves the previous handler and calls it). The assignment also replaces the handler the
 * bootloader page installed, which shows the "content is not loaded" screen.
 *
 * An assignment can be overwritten, though. The Geotab ELD Info add-in assigns window.onerror without
 * calling the previous handler, which would silently end all error reporting for the session. So Drive
 * also registers an error event listener, which an assignment cannot replace.
 *
 * Both paths can see the same error (always, when nobody replaced the handler; also when an add-in
 * chains it), so an error event is reported once: the first path to see it records the event.
 * A window.onerror(...) call counts as that event only when it is made while the event is being
 * dispatched on the window (window.event) and its arguments are the event's own values. Any other call
 * is an independent report and is made every time: a direct call outside a dispatch (Fleet Profit
 * Center), an add-in reporting an error it caught before or after forwarding the original, or a
 * callback of a failed resource load. An add-in that alters the arguments when it chains is therefore
 * reported twice rather than lost.
 *
 * Both paths pass the browser's values through unchanged, exactly as the assignment alone did.
 *
 * Only errors raised on the window reach either path, as before: failed resource loads on elements do
 * not bubble to it. An error event that is not an ErrorEvent is passed on as the message, which is what
 * window.onerror received in that case.
 */
export function listenForJavaScriptErrors(target: Window = window): () => void {
    const reportedEvents = new WeakSet<Event>();
    const isFirstSight = (event: Event): boolean => {
        if (reportedEvents.has(event)) {
            return false;
        }
        reportedEvents.add(event);
        return true;
    };

    const describesEvent = (event: Event | undefined, message: unknown, url: unknown, lineNumber: unknown, column: unknown, error: unknown): event is Event => {
        if (!event || event.type !== "error" || event.target !== target) {
            return false;
        }
        if (!("message" in event)) {
            return message === event;
        }
        const { message: eventMessage, filename, lineno, colno, error: eventError } = event as ErrorEvent;
        return message === eventMessage && url === filename && lineNumber === lineno && column === colno && error === eventError;
    };

    const onWindowError: OnErrorEventHandler = (message, url, lineNumber, column, error) => {
        const event = target.event;
        if (describesEvent(event, message, url, lineNumber, column, error) && !isFirstSight(event)) {
            return;
        }
        handleJS(message, url as string | null, lineNumber as number | null, column as number | null, error);
    };
    const onErrorEvent = (event: Event) => {
        if (!isFirstSight(event)) {
            return;
        }
        if ("message" in event) {
            const { message, filename, lineno, colno, error } = event as ErrorEvent;
            handleJS(message, filename, lineno, colno, error);
        } else {
            handleJS(event, null, null, null, undefined);
        }
    };

    target.onerror = onWindowError;
    target.addEventListener("error", onErrorEvent);
    return () => {
        target.removeEventListener("error", onErrorEvent);
        if (target.onerror === onWindowError) {
            target.onerror = null;
        }
    };
}

export function handleJSNative(error: Error, silenceError?: boolean, tag?: string): void {
    dispatcher.trigger(DispatcherEvents.ErrorHandleJSNative, error, silenceError, tag);
}

export function handleApi(errorObject: any, silenceError?: boolean): void {
    dispatcher.trigger(DispatcherEvents.ErrorHandleApi, errorObject, silenceError);
}

export function toError(value: unknown): Error {
    return value instanceof Error ? value : new Error(String(value));
}

export function handleUnhandledRejection(error) {
    dispatcher.trigger(DispatcherEvents.ErrorHandleUnhandledRejection, error);
}

export function provideFeedback(): void {
    dispatcher.trigger(DispatcherEvents.ProvideFeedback);
}

export function cleanupSensitiveString(message: string): string {
    return cleanSessionAndPassword(message);
}

export function cleanupSensitiveData(data: any): any {
    if (!data) {
        return data;
    }

    if (typeof (data) === "string") {
        return cleanupSensitiveString(data);
    }

    if (Array.isArray(data)) {
        return data.map(cleanupSensitiveData);
    }

    if (typeof (data) === "object") {
        let keys = Object.keys(data);
        let isUserSensitiveField = key => key === "password" || key === "sessionId";

        keys.forEach(function(key) {
            if (isUserSensitiveField(key)) {
                delete data[key];
            } else {
                data[key] = cleanupSensitiveData(data[key]);
            }
        });

        return data;
    }

    return data;
}
